import React, { useState, useEffect } from 'react';
import { X, Send, Users, User, Loader2, Bell, ChevronDown, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';

const NotificationCenter = ({ onClose, isDark }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [recipientType, setRecipientType] = useState('broadcast');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [sending, setSending] = useState(false);
  const [sentHistory, setSentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get('/notifications/admin/clients-list');
        setClients(res.data?.clients || []);
        setFilteredClients(res.data?.clients || []);
      } catch (e) { /* silent */ }
    };
    const fetchHistory = async () => {
      try {
        setLoadingHistory(true);
        const res = await api.get('/notifications/admin/sent');
        setSentHistory(res.data?.notifications || []);
      } catch (e) { /* silent */ }
      finally { setLoadingHistory(false); }
    };
    fetchClients();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (!searchQ) {
      setFilteredClients(clients);
    } else {
      const q = searchQ.toLowerCase();
      setFilteredClients(clients.filter(c =>
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.company_name || '').toLowerCase().includes(q)
      ));
    }
  }, [searchQ, clients]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (recipientType === 'individual' && !selectedClient) {
      toast.error('Select a recipient');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/notifications/send', {
        title: title.trim(),
        message: message.trim(),
        recipient_type: recipientType,
        recipient_id: selectedClient?.id || null,
        priority,
      });
      const count = res.data?.recipients || 0;
      toast.success(`Notification sent to ${count} recipient${count !== 1 ? 's' : ''}`);
      setTitle('');
      setMessage('');
      setSelectedClient(null);
      // Refresh history
      const histRes = await api.get('/notifications/admin/sent');
      setSentHistory(histRes.data?.notifications || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const clientName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="notification-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-3xl rounded-t-2xl shadow-2xl transform transition-transform duration-500 animate-slide-up ${
        isDark ? 'bg-slate-900 border border-white/10 border-b-0' : 'bg-white border border-gray-200 border-b-0'
      }`} style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h2 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>Notification Center</h2>
              <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Send notifications to clients</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
            <X className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          {/* Recipient Type */}
          <div className="flex gap-2">
            <button
              onClick={() => { setRecipientType('broadcast'); setSelectedClient(null); }}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                recipientType === 'broadcast'
                  ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                  : isDark ? 'border-white/10 text-white/60 hover:border-white/20' : 'border-gray-200 text-slate-600 hover:border-gray-300'
              }`}
              data-testid="broadcast-btn"
            >
              <Users className="w-4 h-4" /> Broadcast to All
            </button>
            <button
              onClick={() => setRecipientType('individual')}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                recipientType === 'individual'
                  ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                  : isDark ? 'border-white/10 text-white/60 hover:border-white/20' : 'border-gray-200 text-slate-600 hover:border-gray-300'
              }`}
              data-testid="individual-btn"
            >
              <User className="w-4 h-4" /> Individual Client
            </button>
          </div>

          {/* Client Picker */}
          {recipientType === 'individual' && (
            <div className="relative">
              <button
                onClick={() => setShowClientPicker(!showClientPicker)}
                className={`w-full flex items-center justify-between p-3 rounded-xl border ${
                  isDark ? 'border-white/10 bg-white/5 text-white' : 'border-gray-200 bg-gray-50 text-slate-700'
                }`}
                data-testid="client-picker-btn"
              >
                <span className={selectedClient ? '' : (isDark ? 'text-white/40' : 'text-slate-400')}>
                  {selectedClient ? clientName(selectedClient) : 'Select a client...'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showClientPicker && (
                <div className={`absolute z-10 mt-1 w-full rounded-xl border shadow-xl max-h-60 overflow-y-auto ${
                  isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-gray-200'
                }`}>
                  <div className="p-2 sticky top-0 bg-inherit">
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
                      <Input
                        placeholder="Search clients..."
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        className={`pl-9 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`}
                      />
                    </div>
                  </div>
                  {filteredClients.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedClient(c); setShowClientPicker(false); setSearchQ(''); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        isDark ? 'hover:bg-white/10 text-white/80' : 'hover:bg-gray-50 text-slate-700'
                      }`}
                    >
                      <span className="font-medium">{clientName(c)}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{c.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Priority */}
          <div className="flex gap-2">
            {['low', 'normal', 'high', 'urgent'].map((p) => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${
                  priority === p
                    ? p === 'urgent' ? 'bg-red-500 text-white'
                    : p === 'high' ? 'bg-orange-500 text-white'
                    : p === 'normal' ? 'bg-blue-500 text-white'
                    : 'bg-slate-500 text-white'
                    : isDark ? 'bg-white/5 text-white/50 hover:bg-white/10' : 'bg-gray-100 text-slate-500 hover:bg-gray-200'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Title */}
          <Input
            placeholder="Notification title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : ''}
            data-testid="notif-title-input"
          />

          {/* Message */}
          <textarea
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className={`w-full rounded-xl border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/50 ${
              isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'border-gray-200 text-slate-700 placeholder-slate-400'
            }`}
            data-testid="notif-message-input"
          />

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0 h-12"
            data-testid="send-notification-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {recipientType === 'broadcast' ? 'Send to All Clients' : 'Send Notification'}
          </Button>

          {/* Recent Sent */}
          <div className={`border-t pt-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Recently Sent</h4>
            {loadingHistory ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
            ) : sentHistory.length === 0 ? (
              <p className={`text-sm text-center py-4 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>No notifications sent yet</p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {sentHistory.slice(0, 10).map((n) => (
                  <div key={n.id} className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className="flex-1 min-w-0">
                      <span className={`font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{n.title}</span>
                      <span className={`ml-2 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                        {n.recipient_type === 'broadcast' ? '(Broadcast)' : ''}
                      </span>
                    </div>
                    <Badge className={`ml-2 text-[10px] ${n.is_read ? 'bg-emerald-500/20 text-emerald-500' : 'bg-slate-500/20 text-slate-500'}`}>
                      {n.is_read ? 'Read' : 'Unread'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slideUp 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default NotificationCenter;
